// frontend/src/components/SearchBar/SearchBar.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SearchBar.module.css";
import { search } from "../../service/searchService";

const SearchBar = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{
    series: any[];
    products: any[];
  }>({ series: [], products: [] });
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      if (query.length < 2) {
        setResults({ series: [], products: [] });
        return;
      }

      setLoading(true);
      try {
        const data = await search(query);
        setResults(data);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(fetchResults, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const highlightMatch = (text: string, searchQuery: string) => {
    if (!searchQuery) return text;

    const regex = new RegExp(`(${searchQuery})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className={styles.highlight}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const handleSeriesClick = (seriesId: string) => {
    navigate(`/series/${seriesId}`);
    setIsFocused(false);
    setQuery("");
  };

  const handleProductClick = (seriesId: string, productId: string) => {
    navigate(`/series/${seriesId}/product/${productId}`);
    setIsFocused(false);
    setQuery("");
  };

  const hasResults = results.series.length > 0 || results.products.length > 0;

  return (
    <div className={styles.searchContainer} ref={containerRef}>
      <div className={styles.searchInputContainer}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder="Search for series or products..."
          className={`${styles.searchInput} ${
            results.series.length > 0 || results.products.length > 0
              ? styles.resultInputActive
              : ""
          }`}
        />
        {loading && <div className={styles.spinner}></div>}
      </div>

      {isFocused && (query.length >= 2 || hasResults) && (
        <div className={styles.resultsDropdown}>
          {loading ? (
            <div className={styles.loading}>Searching...</div>
          ) : (
            <>
              {results.series.length === 0 && results.products.length === 0 ? (
                <div className={styles.noResults}>No results found</div>
              ) : (
                <>
                  {results.series.length > 0 && (
                    <div className={styles.resultsSection}>
                      <div className={styles.sectionHeader}>Series</div>
                      {results.series.map((series) => (
                        <div
                          key={series._id}
                          className={styles.resultItem}
                          onClick={() => handleSeriesClick(series.id)}
                        >
                          <div className={styles.resultTitle}>
                            {highlightMatch(series.title, query)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {results.products.length > 0 && (
                    <div className={styles.resultsSection}>
                      <div className={styles.sectionHeader}>Products</div>
                      {results.products.map((product) => (
                        <div
                          key={product._id}
                          className={styles.resultItem}
                          onClick={() =>
                            handleProductClick(product.series.id, product._id)
                          }
                        >
                          <div className={styles.resultTitle}>
                            {highlightMatch(product.name, query)}
                          </div>
                          <div className={styles.resultSubtitle}>
                            in {product.series.title}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
